/**
 * Admin Panel Component
 *
 * Full-page overlay with sidebar navigation (matching Settings pattern)
 * Tabs: Users, Invitations, SMTP, Storage, Analytics
 */

import { useState, useEffect } from 'react';
import { Users, Server, HardDrive, BarChart3, Coins, X, ScrollText, Bot, Cpu, Webhook, Image } from 'lucide-react';
import UserManagement from './UserManagement';
import SMTPStatusPanel from './SMTPStatusPanel';
import { UploadSettingsPanel } from './UploadSettingsPanel';
import { AnalyticsPage } from '@/pages/AnalyticsPage';
import { AISettingsPanel } from './AISettingsPanel';
import { AssistantManagement } from './AssistantManagement';
import { WebhookManagement } from './WebhookManagement';
import { GifSettingsPanel } from './GifSettingsPanel';
import { AssistantUsageDashboard } from './AssistantUsageDashboard';

interface AdminPanelProps {
  isOpen: boolean;
  onClose: () => void;
  initialSection?: Section;
  onShowChangelog?: () => void;
}

type Section = 'users' | 'smtp' | 'storage' | 'analytics' | 'ai' | 'assistants' | 'ai-usage' | 'webhooks' | 'gifs';

interface SectionItem { id: Section; label: string; icon: typeof Users }
interface SectionGroup { group: string; items: SectionItem[] }

const sectionGroups: SectionGroup[] = [
  {
    group: 'Администрация',
    items: [
      { id: 'users', label: 'Потребители', icon: Users },
      { id: 'smtp', label: 'SMTP настройки', icon: Server },
      { id: 'storage', label: 'Съхранение', icon: HardDrive },
      { id: 'assistants', label: 'Асистенти', icon: Bot },
      { id: 'ai-usage', label: 'Употреба', icon: Coins },
      { id: 'analytics', label: 'Аналитика', icon: BarChart3 },
    ],
  },
  {
    group: 'Интеграции',
    items: [
      { id: 'ai', label: 'AI настройки', icon: Cpu },
      { id: 'gifs', label: 'GIF интеграция', icon: Image },
      { id: 'webhooks', label: 'Уебхукове', icon: Webhook },
    ],
  },
];

const allSections = sectionGroups.flatMap((g) => g.items);

export const AdminPanel = ({
  isOpen,
  onClose,
  initialSection = 'users',
  onShowChangelog,
}: AdminPanelProps) => {
  const [activeSection, setActiveSection] = useState<Section>(initialSection);

  // Reset to initial section when modal opens (adjust state during render on change)
  const openKey = isOpen ? `open:${initialSection}` : 'closed';
  const [prevOpenKey, setPrevOpenKey] = useState(openKey);
  if (openKey !== prevOpenKey) {
    setPrevOpenKey(openKey);
    if (isOpen) {
      setActiveSection(initialSection);
    }
  }

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const activeSectionData = allSections.find((s) => s.id === activeSection);

  // Users and Analytics need full width, others use constrained width
  const needsFullWidth = activeSection === 'users' || activeSection === 'analytics' || activeSection === 'assistants' || activeSection === 'ai-usage' || activeSection === 'webhooks';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Администрация"
      className="fixed inset-0 z-50 bg-gray-100 dark:bg-dark-bg-primary animate-modal-in"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Close button -- top right */}
      <button
        onClick={onClose}
        className="fixed top-6 right-8 z-50 flex items-center gap-2 group"
        aria-label="Затвори администрация"
      >
        <span className="text-[10px] text-gray-400 dark:text-dark-text-muted opacity-0 group-hover:opacity-100 transition-opacity select-none">
          Esc
        </span>
        <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-dark-bg-hover hover:bg-gray-300 dark:hover:bg-dark-bg-input flex items-center justify-center transition-colors">
          <X className="w-4 h-4 text-gray-500 dark:text-dark-text-secondary" />
        </div>
      </button>

      {/* Main layout */}
      <div className="max-w-5xl mx-auto h-full flex py-12 px-8 gap-8">
        {/* Left sidebar navigation */}
        <nav className="w-56 flex-shrink-0 flex flex-col" role="navigation" aria-label="Администрация">
          <div className="flex flex-col gap-1 flex-1">
            {sectionGroups.map((group, gi) => (
              <div key={group.group} className={gi > 0 ? 'mt-4' : ''}>
                <h1 className="text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-dark-text-muted px-3 mb-2">
                  {group.group}
                </h1>
                {group.items.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        isActive
                          ? 'bg-white dark:bg-dark-bg-hover text-gray-900 dark:text-dark-text-primary font-medium shadow-sm'
                          : 'text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary hover:bg-white/50 dark:hover:bg-dark-bg-hover/50'
                      }`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <Icon className="w-[18px] h-[18px]" />
                      <span>{section.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}

            {/* Changelog link at the bottom */}
            {onShowChangelog && (
              <div className="mt-auto pt-4 border-t border-gray-200 dark:border-dark-border-primary">
                <button
                  onClick={onShowChangelog}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary hover:bg-white/50 dark:hover:bg-dark-bg-hover/50 transition-colors"
                >
                  <ScrollText className="w-[18px] h-[18px]" />
                  <span>Списък с промени</span>
                </button>
              </div>
            )}
          </div>
        </nav>

        {/* Right content area */}
        <div className="flex-1 overflow-y-auto">
          <div className={needsFullWidth ? '' : 'max-w-2xl'}>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-6">
              {activeSectionData?.label}
            </h2>

            {activeSection === 'users' && <UserManagement />}
            {activeSection === 'smtp' && <SMTPStatusPanel />}
            {activeSection === 'storage' && <UploadSettingsPanel />}
            {activeSection === 'ai' && <AISettingsPanel />}
            {activeSection === 'assistants' && <AssistantManagement />}
            {activeSection === 'ai-usage' && <AssistantUsageDashboard />}
            {activeSection === 'webhooks' && <WebhookManagement />}
            {activeSection === 'gifs' && <GifSettingsPanel />}
            {activeSection === 'analytics' && <AnalyticsPage />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
